/**
 * useGymData — Firestore listeners for gym-scoped data (routes, ascents, ratings, members, users).
 * Extracted from useMainApp for maintainability.
 *
 * Users are fetched only for UIDs that appear in ascents/ratings (active climbers)
 * + current user + friends. Admin components fetch their own full member list.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { useDateKey } from "./useDateKey";
import { climbLogDocId } from "../utils/climbLog";
import { chunkForInQuery } from "../utils";
import { useRangeCache } from "./useRangeCache";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useGym } from "../contexts/GymContext";
import { resolvePlatformCaps } from "@sendly/shared";
import { createProfileStore, sameProfile } from "../utils/profileStore";

// Firestore transport errors that are transient and worth re-subscribing for.
// `permission-denied` is deliberately excluded — it's a real rules/ban denial
// (surfaced via ownMember.banned), handled separately with a one-shot stale-
// token refresh instead of a blind retry loop.
const REATTACH_RETRYABLE = new Set([
  "unavailable", "deadline-exceeded", "internal",
  "cancelled", "resource-exhausted", "aborted", "unknown",
]);
const MAX_REATTACH = 4;

/**
 * Fetch the PUBLIC profile projection for a list of UIDs. Returns an array of
 * { uid, name, photoURL, avatarPreference } — `displayName` is mapped to
 * `name` so callers keep the same field contract they had when this read the
 * private `users` doc.
 *
 * Reads `profiles/{uid}` per uid (get-only; `list:false` makes a
 * `documentId() in` query illegal), all in parallel, through the ONE app-wide
 * profile store (utils/profileStore.js): a profile read in the last 10 minutes
 * costs nothing, an older one is read again — so every surface that asks gets
 * a name at most that old, and none keeps one for the whole session.
 * No PII (email/gymIds/birthYear/…) is returned — those live on the private
 * users doc and are fetched only where genuinely needed (own doc, admin
 * memberContacts, platform admin).
 */
const profileStore = createProfileStore({
  fetchOne: async (uid) => {
    const snap = await getDoc(doc(db, "profiles", uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      uid,
      name: d.displayName || "",
      photoURL: d.photoURL || null,
      avatarPreference: d.avatarPreference || null,
    };
  },
});

export function fetchUsersByUids(uids) {
  return profileStore.load(uids);
}

/** `listener(changedProfiles)` whenever a re-read brings a changed name or avatar. */
export function subscribeProfiles(listener) {
  return profileStore.subscribe(listener);
}

/** What the store holds for `uid` without reading: `undefined` unknown, `null` no profile. */
export function peekProfile(uid) {
  return profileStore.peek(uid);
}

/** `listener(uids)` whenever answers for those uids were stored. */
export function onProfilesSettled(listener) {
  return profileStore.onSettle(listener);
}

// Merge store profiles into a `users` list. The signed-in user's entry follows
// their live `users/{uid}` doc and is never overwritten from the store; `addNew`
// is false for change notifications, which only correct uids already shown.
function mergeProfiles(prev, profiles, ownUid, addNew) {
  const map = new Map(prev.map(u => [u.uid, u]));
  let dirty = false;
  for (const p of profiles) {
    const old = map.get(p.uid);
    if (old ? p.uid === ownUid || sameProfile(old, p) : !addNew) continue;
    map.set(p.uid, old ? { ...old, ...p } : p);
    dirty = true;
  }
  return dirty ? [...map.values()] : prev;
}

// A rating doc is meaningful with a star score or a written comment (text-only
// ratings are stored with stars=0); only the completely empty legacy docs go.
function isMeaningfulRating(r) {
  return Number(r.stars) > 0 || !!(r.comment && String(r.comment).trim());
}

export function useGymData({ authUser, gymId, gymCollection, extraUids = [], timezone = null }) {
  // The signed-in user's doc comes from the app-wide provider. This hook
  // used to keep a second listener on it, without the guards the provider
  // has; now it only reads (for the memberContacts gate) and mirrors.
  const { userData } = useCurrentUser();
  const { ownMember, memberState } = useGym();
  const [routes, setRoutes] = useState([]);
  const [ascents, setAscents] = useState([]); // own ascents (real-time)
  // Today's own climb log — ONE doc, live. Quick-log needs it for the running
  // counter and the double-tap guard; RouteModal reads the per-route repeat
  // entries out of it. Scoped to a single doc id, so it costs one read per
  // change and never grows with gym size.
  const [todayClimbLog, setTodayClimbLog] = useState(null);
  const [myGymStats, setMyGymStats] = useState(null);
  const [members, setMembers] = useState([]);
  // uid -> email, from the gym-admin-readable memberContacts subcollection.
  // Only populated for gym admins (see the gated listener below); merged into
  // the returned `members` so roster / picker surfaces can show + search email
  // without it living on the public member doc.
  const [memberContacts, setMemberContacts] = useState({});
  const [users, setUsers] = useState([]);
  // Own member doc — from GymProvider, which holds the single listener on it.
  // Kept separate from the `members` list so ban detection works even when the
  // caller's own doc is filtered out of that list. `undefined` means "not yet
  // loaded"; `null` means loaded but doesn't exist.
  const [loading, setLoading] = useState(true);
  // Optimistic per-route ascent-count deltas for the user's OWN just-logged
  // tick (routeId → ±delta). The authoritative count lives on each route doc
  // (`route.ascentCount`, CF-maintained, delivered live by the routes
  // listener); this overlay only bridges the ~1-2s until that round-trip
  // lands, and each entry is cleared as soon as the routes listener delivers
  // that route's updated doc (see u3 below).
  const [ascentCountOverlay, setAscentCountOverlay] = useState({});
  // Delay showing the loading overlay — offline cache resolves in <50ms,
  // so we only show loading if data takes longer than 150ms.
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (!loading) { setShowLoading(false); return; }
    const id = setTimeout(() => setShowLoading(true), 150);
    return () => clearTimeout(id);
  }, [loading]);

  // Bumped on every return to the foreground, so the collecting effect asks the
  // profile store again and names older than its TTL are re-read.
  const [profileTick, setProfileTick] = useState(0);
  // `${uid}|${gymId}` the own-ascents listener last answered for: `ascents`
  // belongs to that climber and gym only (the own overlay below checks it).
  const ownAscentsForRef = useRef(null);

  // Depend on authUser.uid (not authUser object) to avoid re-subscribing on reference changes
  const authUid = authUser?.uid;
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") setProfileTick(n => n + 1); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  // A name re-read by any surface (admin users list, route card) corrects the
  // entry this list already shows.
  useEffect(() => subscribeProfiles(changed => {
    setUsers(prev => mergeProfiles(prev, changed, authUid, false));
  }), [authUid]);

  // ── Listener resilience (D1/D2) ──
  // A transient transport error or a stalled forceLongPolling handshake used to
  // leave the listeners attached-but-silent: no data arrives, nothing recovers,
  // and the user is stuck on an empty shell (only self in the users list) until
  // a full manual pull-to-refresh. Now we re-subscribe a bounded number of
  // times by bumping `reattachNonce` (in the main effect's dep array). The
  // budget is shared between the error path (D1) and the no-data-timeout path
  // (D2); it resets on gym/user change and on the first successful snapshot.
  const [reattachNonce, setReattachNonce] = useState(0);
  const reattachAttemptsRef = useRef(0);
  const reattachTimerRef = useRef(null);
  const permRefreshedRef = useRef(false);
  const requestReattach = useCallback((delayMs) => {
    if (reattachAttemptsRef.current >= MAX_REATTACH) return false;
    if (reattachTimerRef.current) return true; // one already scheduled
    reattachAttemptsRef.current += 1;
    reattachTimerRef.current = setTimeout(() => {
      reattachTimerRef.current = null;
      setReattachNonce((n) => n + 1);
    }, delayMs);
    return true;
  }, []);
  // Reset the reattach budget whenever the target changes; clear any pending
  // timer on change and on unmount.
  useEffect(() => {
    reattachAttemptsRef.current = 0;
    permRefreshedRef.current = false;
    if (reattachTimerRef.current) { clearTimeout(reattachTimerRef.current); reattachTimerRef.current = null; }
    return () => {
      if (reattachTimerRef.current) { clearTimeout(reattachTimerRef.current); reattachTimerRef.current = null; }
    };
  }, [gymId, authUid]);

  // Data loading timeout — if the initial snapshot hasn't arrived in 10s the
  // forceLongPolling channel may have stalled mid-handshake (listeners attached
  // but silent). Re-subscribe a bounded number of times (D2) before finally
  // releasing the loading screen so the user isn't forced to pull-to-refresh.
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      if (authUid && requestReattach(300)) return; // try a fresh subscription
      setLoading(false); // budget exhausted (or signed out) → show the shell
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loading, authUid, requestReattach]);

  const onErr = useCallback((label) => (err) => {
    console.error(`[useGymData] ${label} listener error:`, err.code, err.message);
    const code = err && err.code;
    if (REATTACH_RETRYABLE.has(code)) {
      // Transient transport error → bounded re-subscribe with backoff.
      requestReattach(Math.min(1000 * Math.pow(2, reattachAttemptsRef.current), 8000));
    } else if (code === "permission-denied" && !permRefreshedRef.current && auth.currentUser) {
      // Possibly a stale auth token rather than a real rules denial. Force a
      // single token refresh + one reattach; if it denies again we stop (a
      // genuine ban surfaces via ownMember.banned, not here).
      permRefreshedRef.current = true;
      auth.currentUser.getIdToken(true).catch(() => {}).finally(() => requestReattach(300));
    }
  }, [requestReattach]);

  // Ascents, ratings and climb logs are readable only by an ACTIVE member of the
  // gym (firestore.rules `isActiveGymMember`). On a first visit the member doc
  // is created by useAuth concurrently with these listeners, so subscribing
  // before the SERVER has it is a guaranteed permission-denied — and a one-shot
  // listener error is not retried. The member-only listeners therefore wait for
  // GymContext's `memberState`, which turns "active" only on a server-confirmed
  // snapshot (never on useAuth's own pending write). A string, not the object,
  // so an edit to the member doc (e.g. a notifPush toggle) does not
  // re-subscribe anything. The on-demand reads below (own climb-log range,
  // ranking window, route climbers, the full export read) take the same gate:
  // each returns an empty answer until it opens, and its new identity re-runs
  // the caller's effect then.
  const memberActive = memberState === "active";

  useEffect(() => {
    if (!authUid) { setLoading(false); return; }
    setLoading(true);

    // Active-only routes listener. Archived routes accumulate without bound
    // (gyms cycle ~20 new routes/week → thousands per year), and almost every
    // consumer only renders active routes — map, route list, leaderboards,
    // TV slides, stats. Filtering at the query level keeps Firestore reads
    // bounded by the wall size, not the gym's history.
    //
    // Archived routes are loaded on-demand by the few features that need
    // them: `RoutesTab`'s "Archived" filter, `SetterStats` archived count,
    // and the admin purge / bulk-delete flows. Ascents on archived routes
    // still resolve correctly because every ascent doc carries a snapshot
    // of its route's display fields (`routeName`, `routeDifficulty`,
    // `routeHoldColor`, `routeDiscipline`) — see `utils/ascentRoute.js`.
    let routesInit = false;
    const routesQuery = query(gymCollection("routes"), where("active", "==", true));
    const u3 = onSnapshot(routesQuery, s => {
      if (routesInit && s.docChanges().length === 0) return;
      routesInit = true;
      setRoutes(s.docs.map(d => ({ id: d.id, ...d.data() })));
      // The authoritative route.ascentCount just arrived for any changed
      // routes — drop their optimistic overlay so the base value (not base +
      // optimistic) is shown, avoiding a transient double-count.
      const changedIds = s.docChanges().map(c => c.doc.id);
      if (changedIds.length) {
        setAscentCountOverlay(prev => {
          let next = prev, mutated = false;
          for (const id of changedIds) {
            if (id in next) {
              if (!mutated) { next = { ...prev }; mutated = true; }
              delete next[id];
            }
          }
          return mutated ? next : prev;
        });
      }
    }, onErr("routes"));

    let membersInit = false;
    const u7 = onSnapshot(gymCollection("members"), s => {
      if (membersInit && s.docChanges().length === 0) return;
      membersInit = true;
      // Drop any `email` still sitting on the public member doc (a pre-fix
      // syncMemberDisplayName leak): `members[].email` must come ONLY from the
      // gated memberContacts merge below, or every roster / picker surface
      // shows — and relies on — the leaked copy.
      setMembers(s.docs.map(d => {
        const { email: _leakedEmail, ...data } = d.data();
        return { uid: d.id, ...data };
      }));
    }, onErr("members"));

    return () => { u3(); u7(); };
    // reattachNonce: bumping it (D1/D2) tears down and re-subscribes all
    // listeners — the recovery path for a stalled/errored channel.
  }, [authUid, gymId, reattachNonce, onErr]);

  // User-scoped ascent listener — only the current user's ascents in real-time.
  // Declared after the main effect so a reattach (which sets loading=true there)
  // is followed by this effect's own re-subscribe in the same commit.
  useEffect(() => {
    if (!authUid || !gymId) return;
    if (memberState !== "active") {
      // Not a member (yet), or banned: nothing this user may read. Release the
      // loading state once that is KNOWN; while the member doc is still loading,
      // keep waiting — the effect re-runs when `memberState` settles.
      if (memberState === "none") { setAscents([]); setLoading(false); }
      return;
    }
    let ascentsInit = false;
    const ascentsQuery = query(gymCollection("ascents"), where("userId", "==", authUid));
    const unsub = onSnapshot(ascentsQuery, s => {
      if (ascentsInit && s.docChanges().length === 0) return;
      ascentsInit = true;
      ownAscentsForRef.current = `${authUid}|${gymId}`;
      setAscents(s.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
      reattachAttemptsRef.current = 0; // data arrived → reset the reattach budget
    }, onErr("ascents"));
    return () => unsub();
  }, [authUid, gymId, reattachNonce, memberState, onErr]);

  // Keep the cached `users` entry for the signed-in user in step with their
  // live doc. The leaderboard, the friend ranking and the activity feed all
  // render names out of that list, so without this a display-name change only
  // showed up after a reload, or once a new ascent forced a re-fetch.
  // The own entry is also CREATED from the live doc when the public profile is
  // not readable yet (a fresh sign-up: the `syncPublicProfile` trigger writes
  // it seconds later) — the climber's own row must not wait for it.
  useEffect(() => {
    const uid = userData?.uid;
    if (!uid) return;
    setUsers(prev => {
      const idx = prev.findIndex(u => u.uid === uid);
      if (idx < 0) {
        return [...prev, {
          uid, name: userData.name || "",
          photoURL: userData.photoURL || null, avatarPreference: userData.avatarPreference || null,
        }];
      }
      const next = prev.slice();
      next[idx] = { ...next[idx], ...userData };
      return next;
    });
  }, [userData]);
  // ── Today's own climb log ────────────────────────────────────────────────
  // A single-doc listener (`climbLogs/{uid}_{today}`), deliberately separate
  // from the main effect so a midnight rollover re-points only this listener
  // instead of tearing down routes / ascents / members. `todayKey` comes from
  // useDateKey with the GYM's timezone — the same boundary the log id is
  // written with, otherwise a climber near midnight would watch yesterday's doc.
  const todayKey = useDateKey(timezone);
  useEffect(() => {
    if (!authUid || !gymId || memberState !== "active") { setTodayClimbLog(null); return; }
    const ref = doc(db, "gyms", gymId, "climbLogs", climbLogDocId(authUid, todayKey));
    // Local error handler on purpose: the main effect's `onErr` also drives the
    // reattach budget, and a one-doc listener isn't worth tearing down the whole
    // subscription set for. Losing it degrades quick-log to "no live counter",
    // never to wrong data.
    const unsub = onSnapshot(ref, s => {
      setTodayClimbLog(s.exists() ? { id: s.id, ...s.data() } : null);
    }, err => { console.error("todayClimbLog listener", err); });
    return () => unsub();
  }, [authUid, gymId, todayKey, reattachNonce, memberState]);

  // ── Own gym-stats doc (activity totals) ──────────────────────────────────
  // `users/{uid}/gymStats/{gymId}` is maintained by two Cloud Function triggers
  // and carries `climbDates` (date → climb count, sends AND repeats) plus the
  // volume totals. One doc, live: it is what lets the heatmap and the streak
  // count every climb without a range query over the whole history — a streak
  // built from `ascents` breaks the week a climber runs out of new routes,
  // which is exactly the dead-app problem repeats exist to solve.
  useEffect(() => {
    if (!authUid || !gymId) { setMyGymStats(null); return; }
    const unsub = onSnapshot(
      doc(db, "users", authUid, "gymStats", gymId),
      s => setMyGymStats(s.exists() ? s.data() : null),
      err => { console.error("myGymStats listener", err); },
    );
    return () => unsub();
  }, [authUid, gymId, reattachNonce]);

  // ── Climb logs over a date range (stats) ─────────────────────────────────
  // One-shot, user-scoped, date-bounded: ~40 docs for a 12-week window because
  // the unit is a DAY, not a climb. Cached per range key with the same 60s TTL
  // the other scoped fetchers use. Never call this without both bounds — an
  // unbounded query would scan every climber's whole history.
  const climbLogCacheRef = useRef(new Map());
  const fetchClimbLogs = useCallback(async (from, to, force = false) => {
    // Member-only data (see `memberActive` above): an empty answer until the
    // server has the member doc; the new identity then re-runs the callers.
    if (!authUid || !gymId || !memberActive || !from || !to) return [];
    const key = `${from}__${to}`;
    const hit = climbLogCacheRef.current.get(key);
    if (!force && hit && Date.now() - hit.at < 60_000) return hit.data;
    const q = query(
      gymCollection("climbLogs"),
      where("userId", "==", authUid),
      where("date", ">=", from),
      where("date", "<=", to),
    );
    const snap = await getDocs(q);
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    climbLogCacheRef.current.set(key, { at: Date.now(), data });
    return data;
  }, [authUid, gymId, gymCollection, memberActive]);

  // Subscribe to memberContacts (email per member) for those allowed to see
  // roster emails: gym admins, competition/event-management cap holders, and
  // designated competition organizers (member.compOrganizer, maintained by the
  // syncMemberCompOrganizer trigger). Email was split out of the public member
  // doc into this subcollection; the gate mirrors the memberContacts read rule
  // so we never attempt a listen the rules would deny.
  // A platform operator holding `platform.gym.adminAny` can list every gym's
  // memberContacts via the rule, regardless of whether they hold a gymAdmin
  // member doc in this gym — so the platform term belongs here too, not just
  // the member role. Without it such an operator never starts the listener and
  // sees no roster email.
  //
  // This is the RESOLVED CAPABILITY, not `platformRole === "platformAdmin"`.
  // The rule stopped being a role comparison in P8, and the role test was wrong
  // in both directions against it: an operator whose `adminAny` was revoked
  // still started a listener the rules deny, and one granted the cap directly
  // through `platformCaps` (no `platformRole`) never started one the rules
  // would have allowed.
  const hasGymAdminAnyCap = useMemo(
    () => resolvePlatformCaps(userData).has("platform.gym.adminAny"),
    [userData]
  );
  // The member-role term waits for the server-confirmed membership: the rule
  // reads the member doc, so a role seen only on a pending write is a denial.
  const canSeeMemberContacts = hasGymAdminAnyCap || (memberState === "active" && !!ownMember && (
    ownMember.role === "gymAdmin"
    || ownMember.compOrganizer === true
    || (ownMember.extraCaps || []).some(c => c === "competitions.manage" || c === "events.manage")
  ));
  useEffect(() => {
    if (!authUid || !gymId || !canSeeMemberContacts) { setMemberContacts({}); return; }
    const unsub = onSnapshot(gymCollection("memberContacts"), s => {
      const map = {};
      s.docs.forEach(d => { const e = d.data()?.email; if (e) map[d.id] = e; });
      setMemberContacts(map);
    }, (err) => console.error("[useGymData] memberContacts listener error:", err.code, err.message));
    return () => unsub();
  }, [authUid, gymId, canSeeMemberContacts]);

  // The date-range read behind the leaderboard cache (the admin charts read their
  // climb-log range in `useAdminStatsSources`): an inclusive
  // "YYYY-MM-DD" window on a single field, so no composite index is needed.
  // Both bounds null means "all" — which still reads everything, but only when
  // the user explicitly picks that period.
  const readDateRange = useCallback(async (collectionName, from, to) => {
    const constraints = [];
    if (from) constraints.push(where("date", ">=", from));
    if (to) constraints.push(where("date", "<=", to));
    const col = gymCollection(collectionName);
    const snap = await getDocs(constraints.length ? query(col, ...constraints) : col);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, [gymCollection]);

  // Fetch the WHOLE gym ascents collection on-demand. This is the only
  // full-history read left in the app and it is reserved for the admin
  // stats charts (traffic / grade distribution / user growth over time —
  // which genuinely need every tick) and the gym data export. Everything
  // else now reads a scoped slice instead (see fetchRankingAscents /
  // fetchAscentsForRoutes below and route.ascentCount for counts) so the
  // common in-app views (leaderboard, route chips, setter stats) no longer
  // download years of history just to render a small window.
  //
  // Cached with a short TTL rather than once-per-session: only the CURRENT
  // user's own ascents are live-merged below, so a permanent cache made the
  // charts go stale mid-session — any OTHER climber's ascents logged after
  // the first fetch stayed invisible until a hard page refresh. The TTL
  // refreshes on revisit, the throttle prevents refetch spam, and the
  // in-flight guard prevents a double-fetch from a quick double mount.
  // (`useRangeCache` owns all five of those mechanics.)
  // The viewer's own live ascents, laid over every answer of the two ascent
  // caches below as it lands (`mergeOwn`, set further down in this render). A
  // read that left before the newest own tick answers without it; the patch
  // effect cannot fix that — it skips a cache not loaded yet, and a landing
  // answer replaces what it patched. Without this the climber's own fresh send
  // was missing from the leaderboard until the next re-read (a tick logged
  // right after opening the gym, while the background prefetch was out).
  const ownOverlayRef = useRef(null);
  const overlayOwn = useCallback((value) => (ownOverlayRef.current ? ownOverlayRef.current(value) : value), []);
  const [allAscents, allAscentsCache, allAscentsApi] = useRangeCache(gymId, {
    label: "fetchAllAscents",
    load: async () => (await getDocs(gymCollection("ascents"))).docs.map(d => ({ id: d.id, ...d.data() })),
    overlay: overlayOwn,
  });
  const fetchAllAscents = useCallback(
    (force = false) => (memberActive ? allAscentsCache(null, null, force) : Promise.resolve([])),
    [allAscentsCache, memberActive],
  );

  // ── Leaderboard: date-range-scoped ascents ──
  // The leaderboard only ever ranks ONE period (today / week / month / …),
  // so it queries just that date window instead of scanning all history.
  // Ascents carry a `date` string ("YYYY-MM-DD", written with the gym TZ),
  // and this is a single-field range query — no composite index needed.
  // Cost scales with the period length, not the gym's age: "today" reads a
  // few dozen docs on a years-old gym. `from`/`to` are inclusive
  // "YYYY-MM-DD" bounds (null = unbounded; both null = "all", which still
  // reads everything but only when the user explicitly picks that period).
  // Cache key is the range itself + a short TTL: switching period refetches
  // the new window, revisiting the same one within the TTL is free, and the
  // own-ascents merge below keeps the viewer's just-logged tick instant.
  const [rankingAscents, fetchRankingRange, rankingApi] = useRangeCache(gymId, {
    label: "fetchRankingAscents",
    load: (from, to) => readDateRange("ascents", from, to),
    overlay: overlayOwn,
  });
  // Gated outside the cache, so a not-yet-member's empty answer is never
  // cached as the period's ranking.
  const fetchRankingAscents = useCallback(
    (from, to, force = false) => (memberActive ? fetchRankingRange(from, to, force) : Promise.resolve([])),
    [fetchRankingRange, memberActive],
  );

  // The admin Statistics sources (the adminStats doc, the climb-log range of
  // the period charts) moved to `useAdminStatsSources`, held at app level so
  // the section keeps them across tab switches (docs/spec/01f-tab-loading.md).

  // The Routes tab needs the climber list for the routes it shows, not the
  // whole gym history. We query by routeId in batches of 30 (the `in` limit)
  // and cache per route, so opening the tab reads only the ascents on the
  // currently-active set (bounded by the wall, not by the gym's age).
  // `fetchedRouteIdsRef` tracks which routes are already loaded so repeat
  // calls (filter/search changes, archived view) only fetch the new ids.
  const [routeAscents, setRouteAscents] = useState(() => new Map()); // routeId → ascents[]
  const fetchedRouteIdsRef = useRef(new Set());
  // Every read still in flight. A call whose ids are all claimed already waits
  // for these instead of resolving at once, so a caller that awaits the fetch
  // to know the chips are in is told the truth.
  const routeReadsInFlightRef = useRef(new Set());
  const readAscentsForRoutes = useCallback(async (need) => {
    try {
      const batches = chunkForInQuery(need);
      const snaps = await Promise.all(batches.map(b =>
        getDocs(query(gymCollection("ascents"), where("routeId", "in", b)))
      ));
      const byRoute = new Map();
      snaps.forEach(snap => snap.docs.forEach(d => {
        const a = { id: d.id, ...d.data() };
        if (!a.routeId) return;
        if (!byRoute.has(a.routeId)) byRoute.set(a.routeId, []);
        byRoute.get(a.routeId).push(a);
      }));
      setRouteAscents(prev => {
        const next = new Map(prev);
        // Seed every requested route (even those with zero ascents) so the
        // chip row renders "no climbers" instead of re-triggering a fetch.
        need.forEach(id => { if (!next.has(id)) next.set(id, []); });
        for (const [rid, list] of byRoute) next.set(rid, list);
        return next;
      });
    } catch (err) {
      console.error("[useGymData] fetchAscentsForRoutes error:", err);
      // Allow a later retry for the ids that failed.
      need.forEach(id => fetchedRouteIdsRef.current.delete(id));
    }
  }, [gymCollection]);
  const fetchAscentsForRoutes = useCallback(async (routeIds) => {
    if (!gymId || !memberActive || !Array.isArray(routeIds) || routeIds.length === 0) return;
    const need = [...new Set(routeIds.filter(id => id && !fetchedRouteIdsRef.current.has(id)))];
    if (need.length === 0) { await Promise.all([...routeReadsInFlightRef.current]); return; }
    // Mark eagerly so a re-render mid-fetch doesn't re-request the same ids.
    need.forEach(id => fetchedRouteIdsRef.current.add(id));
    const read = readAscentsForRoutes(need);
    routeReadsInFlightRef.current.add(read);
    try { await read; } finally { routeReadsInFlightRef.current.delete(read); }
  }, [gymId, memberActive, readAscentsForRoutes]);

  // Rating documents per route, read on demand — the replacement of the old
  // gym-wide `ratings` listener (docs/spec/01f-tab-loading.md, "Route ratings
  // on demand"). Averages and counts do not come from here (they live on the
  // route document); this holds the comment texts and who rated, for the
  // routes a card actually shows. Same shape as the ascent read above: batches
  // of 30 `in` ids, one read per route id for the gym session, a call whose
  // ids are all claimed waits for the reads in flight, a failed batch hands its
  // ids back for a retry. A rating doc is meaningful with a star score OR a
  // written comment (text-only ratings are stored with stars=0); the empty
  // legacy docs are dropped.
  const [routeRatings, setRouteRatings] = useState(() => new Map()); // routeId → rating docs[]
  const fetchedRatingRouteIdsRef = useRef(new Set());
  const ratingReadsInFlightRef = useRef(new Set());
  // The gym a read was started for: a read that settles after a gym switch
  // must not write the old gym's ratings into the new gym's map.
  const ratingsGymRef = useRef(gymId);
  ratingsGymRef.current = gymId;
  const readRatingsForRoutes = useCallback(async (need, forGymId) => {
    try {
      const batches = chunkForInQuery(need);
      const snaps = await Promise.all(batches.map(b =>
        getDocs(query(gymCollection("ratings"), where("routeId", "in", b)))
      ));
      if (ratingsGymRef.current !== forGymId) return;
      const byRoute = new Map();
      snaps.forEach(snap => snap.docs.forEach(d => {
        const r = { id: d.id, ...d.data() };
        if (!r.routeId || !isMeaningfulRating(r)) return;
        if (!byRoute.has(r.routeId)) byRoute.set(r.routeId, []);
        byRoute.get(r.routeId).push(r);
      }));
      setRouteRatings(prev => {
        const next = new Map(prev);
        // Seed every requested route (even those with no rating) so the card
        // renders "no comments" instead of re-triggering a fetch.
        need.forEach(id => { if (!next.has(id)) next.set(id, []); });
        for (const [rid, list] of byRoute) next.set(rid, list);
        return next;
      });
    } catch (err) {
      console.error("[useGymData] fetchRatingsForRoutes error:", err);
      // Allow a later retry for the ids that failed.
      if (ratingsGymRef.current === forGymId) need.forEach(id => fetchedRatingRouteIdsRef.current.delete(id));
    }
  }, [gymCollection]);
  const fetchRatingsForRoutes = useCallback(async (routeIds) => {
    // Ratings are readable by an ACTIVE member only (firestore.rules): for
    // anyone else the answer is "nothing", not a permission-denied read.
    if (!gymId || memberState !== "active" || !Array.isArray(routeIds) || routeIds.length === 0) return;
    const need = [...new Set(routeIds.filter(id => id && !fetchedRatingRouteIdsRef.current.has(id)))];
    if (need.length === 0) { await Promise.all([...ratingReadsInFlightRef.current]); return; }
    // Mark eagerly so a re-render mid-fetch doesn't re-request the same ids.
    need.forEach(id => fetchedRatingRouteIdsRef.current.add(id));
    const read = readRatingsForRoutes(need, gymId);
    ratingReadsInFlightRef.current.add(read);
    try { await read; } finally { ratingReadsInFlightRef.current.delete(read); }
  }, [gymId, memberState, readRatingsForRoutes]);

  // The viewer's own rating, applied to the cached list once the write
  // succeeded (`handleRate`): a comment list the viewer just edited is current
  // without a re-read. Only a route already in the map is touched — a route
  // nobody asked for stays unread, and its first read will include the write.
  // `ratingDoc: null` removes the viewer's rating (cleared).
  const patchRouteRating = useCallback((routeId, ratingDoc, uid) => {
    if (!routeId || !uid) return;
    setRouteRatings(prev => {
      const list = prev.get(routeId);
      if (!list) return prev;
      const others = list.filter(r => r.userId !== uid);
      const next = new Map(prev);
      next.set(routeId, ratingDoc && isMeaningfulRating(ratingDoc) ? [...others, ratingDoc] : others);
      return next;
    });
  }, []);

  // On-demand fetch for archived routes — the main routes listener filters
  // to active===true so archived ones never enter `routes`. Two access
  // patterns coexist:
  //   - **Paginated** (RoutesTab UI): `fetchArchivedRoutes()` loads the
  //     first ARCHIVED_PAGE_SIZE most-recent archived routes;
  //     `loadMoreArchivedRoutes()` appends the next page on demand. Caps
  //     bandwidth and parse cost on gyms with thousands of archived
  //     routes accumulated over years.
  //   - **Full** (SetterStats per-setter aggregates, admin bulk archive /
  //     purge): `fetchAllArchivedRoutes()` ignores the page cap and pulls
  //     everything in a single getDocs. Aggregates / cascade-deletes need
  //     completeness over freshness.
  // Both share the same `archivedRoutes` state. Calling `fetchAllArchived`
  // after a paginated fetch upgrades the cache to "fully loaded" and
  // clears `archivedRoutesHasMore`. Cache lives until `invalidate*` (route
  // active toggles) or gym change.
  const ARCHIVED_PAGE_SIZE = 100;
  const [archivedRoutes, setArchivedRoutes] = useState([]);
  const [archivedRoutesHasMore, setArchivedRoutesHasMore] = useState(false);
  const archivedRoutesLastDocRef = useRef(null);
  const archivedRoutesFullyLoadedRef = useRef(false);
  const archivedRoutesFirstPageFetchedRef = useRef(false);

  const fetchArchivedRoutes = useCallback(async () => {
    if (!gymId) return archivedRoutes;
    // Skip if first page is already loaded OR we have the full set.
    if (archivedRoutesFirstPageFetchedRef.current || archivedRoutesFullyLoadedRef.current) return archivedRoutes;
    try {
      const q = query(
        gymCollection("routes"),
        where("active", "==", false),
        orderBy("createdAt", "desc"),
        limit(ARCHIVED_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setArchivedRoutes(list);
      archivedRoutesLastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      setArchivedRoutesHasMore(snap.size === ARCHIVED_PAGE_SIZE);
      archivedRoutesFirstPageFetchedRef.current = true;
      return list;
    } catch (err) {
      console.error("[useGymData] fetchArchivedRoutes error:", err);
      return [];
    }
  }, [gymId, gymCollection, archivedRoutes]);

  const loadMoreArchivedRoutes = useCallback(async () => {
    if (!gymId) return;
    if (!archivedRoutesHasMore || archivedRoutesFullyLoadedRef.current) return;
    const lastDoc = archivedRoutesLastDocRef.current;
    if (!lastDoc) return;
    try {
      const q = query(
        gymCollection("routes"),
        where("active", "==", false),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(ARCHIVED_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const more = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setArchivedRoutes(prev => [...prev, ...more]);
      archivedRoutesLastDocRef.current = snap.docs[snap.docs.length - 1] || lastDoc;
      setArchivedRoutesHasMore(snap.size === ARCHIVED_PAGE_SIZE);
    } catch (err) {
      console.error("[useGymData] loadMoreArchivedRoutes error:", err);
    }
  }, [gymId, gymCollection, archivedRoutesHasMore]);

  const fetchAllArchivedRoutes = useCallback(async () => {
    if (!gymId) return archivedRoutes;
    if (archivedRoutesFullyLoadedRef.current) return archivedRoutes;
    try {
      const q = query(gymCollection("routes"), where("active", "==", false));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setArchivedRoutes(list);
      archivedRoutesFullyLoadedRef.current = true;
      archivedRoutesFirstPageFetchedRef.current = true;
      archivedRoutesLastDocRef.current = null;
      setArchivedRoutesHasMore(false);
      return list;
    } catch (err) {
      console.error("[useGymData] fetchAllArchivedRoutes error:", err);
      return [];
    }
  }, [gymId, gymCollection, archivedRoutes]);

  // ── Routes taken down since a date — the archived half of "which routes were
  //    on the wall during period X" ────────────────────────────────────────
  // The active listener only knows TODAY's wall, so a past period's route
  // stock needs the routes that have come down since the period began.
  // Deliberately NOT the archive view's full fetch: this is bounded by the
  // period, so the default (current month) costs a handful of docs.
  //
  // No `active == false` term, and therefore NO composite index: `archivedAt`
  // is set if and only if a route is archived (the invariant every write path
  // now upholds — see `archiveStamp` in useMainApp, and
  // scripts/backfill-archived-at.js which restored it for old data). A stale
  // stamp on an active route would only re-deliver a route the caller already
  // has, and callers key the stock by route id.
  //
  // Capped rather than paged: past the cap the caller cannot trust the
  // denominator, and a ratio computed from an incomplete stock would be
  // plausibly wrong — which is worse than showing no ratio at all.
  const ARCHIVED_SINCE_CAP = 1000;
  const archivedSinceCacheRef = useRef(new Map());

  const fetchRoutesArchivedSince = useCallback(async (from) => {
    if (!gymId || !from) return null;
    const cached = archivedSinceCacheRef.current.get(from);
    if (cached) return cached;
    try {
      // UTC midnight of `from`. The period bounds are gym-local date strings,
      // so a route archived in the small hours of the first day can fall a few
      // hours outside the window — irrelevant against a week-or-longer period.
      const q = query(
        gymCollection("routes"),
        where("archivedAt", ">=", new Date(from + "T00:00:00Z")),
        limit(ARCHIVED_SINCE_CAP)
      );
      const snap = await getDocs(q);
      const result = {
        routes: snap.docs.map(d => ({ id: d.id, ...d.data() })),
        capped: snap.size === ARCHIVED_SINCE_CAP,
      };
      archivedSinceCacheRef.current.set(from, result);
      return result;
    } catch (err) {
      console.error("[useGymData] fetchRoutesArchivedSince error:", err);
      return null;
    }
  }, [gymId, gymCollection]);

  // Invalidate the cache so the next consumer that calls fetchArchivedRoutes
  // hits Firestore again. Triggered by mutations that change a route's
  // active state (reactivate, archive, hard-delete), so the archived view
  // doesn't show stale entries.
  const invalidateArchivedRoutes = useCallback(() => {
    archivedRoutesFirstPageFetchedRef.current = false;
    archivedRoutesFullyLoadedRef.current = false;
    archivedRoutesLastDocRef.current = null;
    setArchivedRoutes([]);
    setArchivedRoutesHasMore(false);
    archivedSinceCacheRef.current.clear();
  }, []);

  // Reset archived cache when gym changes
  useEffect(() => {
    archivedRoutesFirstPageFetchedRef.current = false;
    archivedRoutesFullyLoadedRef.current = false;
    archivedRoutesLastDocRef.current = null;
    setArchivedRoutes([]);
    setArchivedRoutesHasMore(false);
    archivedSinceCacheRef.current.clear();
  }, [gymId]);

  // Reset every on-demand ascent and rating cache when gym changes. The five
  // `useRangeCache` slots reset themselves; these are hand-rolled.
  useEffect(() => {
    fetchedRouteIdsRef.current = new Set(); setRouteAscents(new Map());
    fetchedRatingRouteIdsRef.current = new Set(); ratingReadsInFlightRef.current = new Set(); setRouteRatings(new Map());
  }, [gymId]);

  // Keep the on-demand ascent caches fresh for the viewer's OWN ticks by
  // merging the live own-ascents listener into each loaded cache. Other
  // climbers' ticks rely on the per-cache TTL / refetch; the viewer's own
  // logging always reflects instantly. Each guard skips work until that
  // cache has actually been populated. For routeAscents we only touch routes
  // already in the map (the ones whose chips are on screen).
  const mergeOwn = useCallback((prev) =>
    [...prev.filter(a => a.userId !== authUid), ...ascents], [authUid, ascents]);
  // The same merge on every read that lands later (`overlayOwn` above). Only
  // once the own listener has answered for THIS climber and gym: before that
  // `ascents` is the empty initial value (or the previous gym's), and the merge
  // would strip the own ascents the read just brought.
  ownOverlayRef.current = authUid && ownAscentsForRef.current === `${authUid}|${gymId}` ? mergeOwn : null;
  useEffect(() => {
    if (!authUid) return;
    if (allAscentsApi.isLoaded()) allAscentsApi.patch(mergeOwn);
    if (rankingApi.isLoaded()) rankingApi.patch(mergeOwn);
    setRouteAscents(prev => {
      if (prev.size === 0) return prev;
      const ownByRoute = new Map();
      for (const a of ascents) {
        if (!a.routeId || !prev.has(a.routeId)) continue;
        if (!ownByRoute.has(a.routeId)) ownByRoute.set(a.routeId, []);
        ownByRoute.get(a.routeId).push(a);
      }
      const next = new Map();
      for (const [rid, list] of prev) {
        const others = list.filter(a => a.userId !== authUid);
        next.set(rid, ownByRoute.has(rid) ? [...others, ...ownByRoute.get(rid)] : others);
      }
      return next;
    });
  }, [ascents, authUid]);

  // Per-route ascent counts ("23×" badges) — read from the denormalized
  // route.ascentCount field on the routes we already have (live via the
  // routes listener), merged with the optimistic overlay for the user's own
  // just-logged tick. Zero extra Firestore reads — replaces the old
  // `refreshAscentCounts` full-collection scan that ran on every app load.
  const ascentCounts = useMemo(() => {
    const m = {};
    for (const r of routes) m[r.id] = Number(r.ascentCount) || 0;
    for (const [rid, d] of Object.entries(ascentCountOverlay)) {
      m[rid] = Math.max(0, (m[rid] || 0) + d);
    }
    return m;
  }, [routes, ascentCountOverlay]);

  // Optimistic bump for the user's OWN tick — reflects instantly, then clears
  // when the routes listener delivers the CF-updated route.ascentCount.
  const bumpAscentCount = useCallback((routeId, delta) => {
    if (!routeId || !delta) return;
    setAscentCountOverlay(prev => ({ ...prev, [routeId]: (prev[routeId] || 0) + delta }));
  }, []);

  // Fetch user docs only for active climbers (UIDs in ascents/ratings) + self + friends.
  // The whole set goes to the profile store on every run: it reads only the
  // uids it does not hold fresh, so a repeat run is free and a stale name is
  // re-read. A missing profile (`profiles/{uid}` is written by a trigger a few
  // seconds after sign-up) is asked for again once the store's short
  // missing-TTL lapses, so a new climber does not stay nameless all session.
  const fetchUsers = useCallback(async (activeUids, extras, currentUid) => {
    const uidSet = new Set(activeUids);
    extras.forEach(uid => { if (uid) uidSet.add(uid); });
    if (currentUid) uidSet.add(currentUid);

    if (uidSet.size === 0) { setUsers([]); return; }

    try {
      const fetched = await fetchUsersByUids([...uidSet]);
      // Unchanged answers keep the same array: a new one would re-render for nothing.
      if (fetched.length === 0) return;
      setUsers(prev => mergeProfiles(prev, fetched, currentUid, true));
    } catch (err) {
      console.error("Failed to fetch scoped users:", err);
    }
  }, []);

  // Collect UIDs of climbers we need to name/avatar. The own-ascents
  // listener only carries the viewer's own UID, so the other climbers come
  // from whichever scoped cache is loaded: route ratings, the leaderboard's
  // ranking window, the route chips' per-route ascents, and (admin only)
  // the full allAscents fetch. fetchUsers is incremental, so overlapping
  // sources don't cause duplicate reads.
  useEffect(() => {
    if (!authUid) return;
    const activeUidSet = new Set();
    for (const list of routeRatings.values()) {
      for (const r of list) { if (r.userId) activeUidSet.add(r.userId); }
    }
    rankingAscents.forEach(a => { if (a.userId) activeUidSet.add(a.userId); });
    for (const list of routeAscents.values()) {
      for (const a of list) { if (a.userId) activeUidSet.add(a.userId); }
    }
    allAscents.forEach(a => { if (a.userId) activeUidSet.add(a.userId); });
    fetchUsers([...activeUidSet], extraUids, authUid);
  }, [authUid, routeRatings, rankingAscents, routeAscents, allAscents, extraUids, fetchUsers, profileTick]);

  // Derived flag: the caller has been banned from this gym. Only true when
  // the own-member snapshot has loaded AND explicitly carries `banned: true`.
  // While `ownMember === undefined` we don't know yet — the UI should not
  // flash the BannedScreen in that case.
  const isBanned = ownMember?.banned === true;

  // Merge admin-only contact email into the members list so roster / picker
  // surfaces keep `member.email` (empty for non-admins, who never load it).
  const membersWithContacts = useMemo(() => {
    if (!Object.keys(memberContacts).length) return members;
    return members.map(m => (memberContacts[m.uid] ? { ...m, email: memberContacts[m.uid] } : m));
  }, [members, memberContacts]);

  return {
    routes, ascents, allAscents, members: membersWithContacts, users,
    ownMember, isBanned,
    loading: showLoading, setLoading,
    // The raw flag (no 150 ms display delay): the background warm-up waits for
    // it (docs/spec/01f-tab-loading.md, "Code warm-up from the tab bar").
    dataLoaded: !loading,
    ascentCounts, fetchAllAscents, bumpAscentCount,
    todayClimbLog, todayKey, fetchClimbLogs, myGymStats,
    rankingAscents, fetchRankingAscents, rankingStatus: rankingApi.status,
    routeAscents, fetchAscentsForRoutes,
    routeRatings, fetchRatingsForRoutes, patchRouteRating,
    archivedRoutes, archivedRoutesHasMore, fetchArchivedRoutes, loadMoreArchivedRoutes, fetchAllArchivedRoutes, invalidateArchivedRoutes,
    fetchRoutesArchivedSince,
  };
}
